"use client";

import { useEffect, useRef } from "react";

/**
 * Steuert ein Preline-Overlay (Modal/Offcanvas) aus React-Zustand, ohne das
 * Preline-Markup zu verändern (Plan Regel 10: Overlays mit Daten-/Server-Bezug
 * als React-Baustein). `offen` öffnet/schließt über die Preline-API
 * (HSOverlay.open/close); schließt der Nutzer das Overlay selbst (Schließen-
 * Knopf, Escape, Hintergrund), meldet `beimSchliessen` das an React zurück.
 *
 * Das Schließen wird über das DOM-Ereignis `close.hs.overlay` des Elements gemeldet, nicht über
 * `HSOverlay.on("close", …)`: das hängt an der einzelnen Instanz und ginge verloren, sobald
 * PrelineClient die Sammlung bei einem Seitenwechsel neu aufbaut.
 */
export function usePrelineOverlay<T extends HTMLElement>(offen: boolean, beimSchliessen: () => void) {
  const ref = useRef<T>(null);
  const schliessenRef = useRef(beimSchliessen);

  useEffect(() => {
    schliessenRef.current = beimSchliessen;
  }, [beimSchliessen]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const beiSchliessen = (e: Event) => {
      // Das Ereignis steigt auf: nur das eigene Overlay zählt, nicht ein verschachteltes.
      if (e.target === el) schliessenRef.current();
    };
    el.addEventListener("close.hs.overlay", beiSchliessen);
    return () => el.removeEventListener("close.hs.overlay", beiSchliessen);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let abgebrochen = false;

    void import("preline/non-auto").then(({ HSOverlay }) => {
      if (abgebrochen) return;
      // Beim ersten Laden kann dieser Effekt vor dem PrelineClient laufen (Kind-Effekte
      // zuerst): dann gibt es die Overlay-Sammlung noch nicht, und getInstance wirft.
      // PrelineClient baut eine hier angelegte Instanz beim Neuaufbau sauber ab (destroy).
      const sammlung = (window as unknown as { $hsOverlayCollection?: unknown[] }).$hsOverlayCollection;
      if (!sammlung || !HSOverlay.getInstance(el, true)) HSOverlay.autoInit();
      if (offen) HSOverlay.open(el);
      else HSOverlay.close(el);
    });

    return () => {
      abgebrochen = true;
    };
  }, [offen]);

  return ref;
}
